import { useEffect, useState } from "react";
import { toast } from "sonner";
import { FileText, PackageMinus, Search } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { useAuth, hasRole } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { useLiveRefresh } from "@/lib/live";
import JobLink from "@/components/JobLink";
import { EmptyRow, Pager, Panel, Pill, RollsLink, SubTabs, currentFy, daysLate, previousFy, qty } from "@/components/stores/storesUi";
import { IssueDialog } from "@/components/stores/StoreDialogs";
import { StoresSheetDialog } from "@/components/stores/StoresSheet";

const LIMIT = 50;

/** BPRs given to stores for raw material (process flow 4.1), most urgent first. */
export default function JobsTab() {
  const { user } = useAuth();
  const canIssue = hasRole(user, "production_manager");
  const thisFy = currentFy();
  const [state, setState] = useState("pending");
  const [fy, setFy] = useState(thisFy);
  const [q, setQ] = useState("");
  const [skip, setSkip] = useState(0);
  const [data, setData] = useState(null);
  const [issueFor, setIssueFor] = useState(null);
  const [sheetFor, setSheetFor] = useState(null);

  const load = (at = skip, quiet = false) => {
    const params = { state, skip: at, limit: LIMIT };
    if (fy !== "all") params.fy = fy;
    if (q.trim()) params.q = q.trim();
    return api.get("/stores/jobs", { params })
      .then(({ data }) => setData(data))
      .catch((e) => !quiet && toast.error(formatApiError(e)));
  };
  const search = () => { setSkip(0); load(0); };
  useEffect(search, [state, fy]); // eslint-disable-line react-hooks/exhaustive-deps
  const reload = useLiveRefresh(["stores", "jobs"], () => load(skip, true));

  const c = data?.counts || {};
  const rows = data?.items || [];

  return (
    <div className="space-y-4">
      <SubTabs
        value={state}
        onChange={setState}
        testid="jobs-state"
        options={[
          { value: "pending", label: `Waiting for material${data ? ` · ${(c.to_issue || 0) + (c.partial || 0)}` : ""}` },
          { value: "to_issue", label: `Not issued${data ? ` · ${c.to_issue || 0}` : ""}` },
          { value: "partial", label: `Part issued${data ? ` · ${c.partial || 0}` : ""}` },
          { value: "issued", label: `Issued${data ? ` · ${c.issued || 0}` : ""}` },
        ]}
      />

      <div className="border border-neutral-200 bg-white p-4 flex flex-wrap items-end gap-3">
        <div className="flex-1 min-w-56">
          <label className="label-caps block mb-1">Search</label>
          <div className="relative">
            <Search size={14} className="absolute left-2.5 top-2.5 text-neutral-400" />
            <Input data-testid="jobs-search" value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && search()}
              className="pl-8 rounded-sm" placeholder="BPR, customer, product, material…" />
          </div>
        </div>
        <div>
          <label className="label-caps block mb-1">Year</label>
          <Select value={fy} onValueChange={setFy}>
            <SelectTrigger data-testid="jobs-fy" className="rounded-sm w-40"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value={thisFy}>FY {thisFy}</SelectItem>
              <SelectItem value={previousFy(thisFy)}>FY {previousFy(thisFy)}</SelectItem>
              <SelectItem value="all">All years</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <Button variant="outline" className="rounded-sm" onClick={search}>Apply</Button>
      </div>

      <Panel>
        <div className="overflow-x-auto">
          <table className="w-full text-sm [&_td]:whitespace-nowrap [&_th]:whitespace-nowrap" data-testid="jobs-table">
            <thead className="bg-neutral-50 label-caps text-[10px]">
              <tr>
                <th className="text-right px-3 py-2.5" title="Place in the Stores queue (the jobs at Stores now)">#</th>
                <th className="text-left px-3 py-2.5">BPR</th>
                <th className="text-left px-3 py-2.5">Job</th>
                <th className="text-left px-3 py-2.5">Due</th>
                <th className="text-left px-3 py-2.5">Material</th>
                <th className="text-right px-3 py-2.5">Required</th>
                <th className="text-right px-3 py-2.5">Issued</th>
                <th className="text-right px-3 py-2.5">In stock</th>
                <th className="text-left px-3 py-2.5">Stores</th>
                <th className="w-16"></th>
                {canIssue && <th className="w-20"></th>}
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && <EmptyRow cols={canIssue ? 11 : 10} loading={!data}>No jobs here.</EmptyRow>}
              {rows.map((j) => {
                const late = j.stores_state !== "issued" ? daysLate(j.due_date) : null;
                const short = j.planned_item && j.remaining != null && j.in_stock < j.remaining;
                return (
                  <tr key={j.bpr_no} className="border-t border-neutral-100 data-row" data-testid={`jobs-row-${j.bpr_no}`}>
                    <td className="px-3 py-2 text-right font-mono-num text-xs font-semibold text-neutral-500">{j.queue_no ? `#${j.queue_no}` : ""}</td>
                    <td className="px-3 py-2">
                      <div className="font-mono-num text-xs font-semibold"><JobLink id={j.id} number={j.bpr_no} tab="materials" /></div>
                      <div className="text-[11px] text-neutral-500 font-mono-num">{j.bpr_issue_date ? String(j.bpr_issue_date).slice(0, 10) : ""}</div>
                    </td>
                    <td className="px-3 py-2">
                      <div className="text-sm truncate max-w-[13rem]" title={j.customer_name}>{j.customer_name}</div>
                      <div className="text-[11px] text-neutral-500 truncate max-w-[13rem]" title={j.product_name}>{j.product_name}</div>
                    </td>
                    <td className="px-3 py-2">
                      <div className="font-mono-num text-xs">{j.due_date ? String(j.due_date).slice(0, 10) : "—"}</div>
                      {late && <div className="text-[11px] text-rose-700">{late} day{late === 1 ? "" : "s"} late</div>}
                    </td>
                    <td className="px-3 py-2">
                      {j.planned_item
                        ? <RollsLink q={j.planned_item} className="font-mono-num text-xs truncate max-w-[9rem]" />
                        : <div className="font-mono-num text-xs truncate max-w-[9rem]" title={j.material}>{j.material || "—"}</div>}
                      {!j.planned_item && <div className="text-[11px] text-neutral-500">no width on card</div>}
                    </td>
                    <td className="px-3 py-2 text-right font-mono-num">{j.required ? `${qty(j.required, 0)} m` : "—"}</td>
                    <td className="px-3 py-2 text-right font-mono-num">
                      {j.issued > 0 && `${qty(j.issued, 0)} m`}
                      {j.issued < 0 && (
                        <div className="text-neutral-500" title="Issued before the stores opening balance; only the return is in the ledger">
                          <div className="font-sans text-xs">before opening</div>
                          <div className="text-[11px]">{qty(-j.issued, 0)} m back</div>
                        </div>
                      )}
                      {!j.issued && <span className="text-neutral-300">—</span>}
                    </td>
                    <td className={`px-3 py-2 text-right font-mono-num ${short ? "text-rose-700" : ""}`}>
                      {j.in_stock == null ? "—" : `${qty(j.in_stock, 0)} m`}
                      {j.planned_item && (
                        <div className={`text-[11px] ${short ? "" : "text-neutral-500"}`} title={short ? "Less in stock than is still to issue: raise a purchase" : undefined}>
                          {short ? "short" : `${j.rolls_in_stock} roll(s)`}
                        </div>
                      )}
                    </td>
                    <td className="px-3 py-2"><Pill value={j.stores_state} /></td>
                    <td className="px-1 py-2 text-right">
                      <Button size="sm" variant="ghost" className="rounded-sm h-7 px-2 gap-1 text-neutral-600" onClick={() => setSheetFor(j.bpr_no)} data-testid={`jobs-sheet-${j.bpr_no}`}
                        title="Stores sheet (Issuing of Material)">
                        <FileText size={12} /> Sheet
                      </Button>
                    </td>
                    {canIssue && (
                      <td className="pl-1 pr-3 py-2 text-right">
                        {j.stores_state !== "issued" && (
                          <Button size="sm" variant="outline" className="rounded-sm h-7 px-2.5 gap-1" onClick={() => setIssueFor(j.bpr_no)} data-testid={`jobs-issue-${j.bpr_no}`}>
                            <PackageMinus size={12} /> Issue
                          </Button>
                        )}
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {data && <Pager skip={data.skip} limit={data.limit} total={data.total} onChange={(s) => { setSkip(s); load(s); }} />}
      </Panel>

      {sheetFor && <StoresSheetDialog bpr={sheetFor} onClose={() => setSheetFor(null)} />}

      {issueFor && (
        <IssueDialog initialBpr={issueFor} onClose={() => setIssueFor(null)} onDone={() => { setIssueFor(null); reload(); }} />
      )}
    </div>
  );
}
